import Sheet from './Sheet'
import { ELEMENTS } from '../../types'
import type { Character } from '../../types'

const SPRITE = (id: string, pose: string) => `/assets/characters/${id}/${pose}.png`
const CHAR_PORTRAITS: Record<string, string> = {
  'kai-voss': SPRITE('kai-voss', 'front'),
  'sela-mourne': SPRITE('sela-mourne', 'front'),
  'drego': SPRITE('drego', 'front'),
  'vara-six': SPRITE('vara-six', 'front'),
  'marshal-coen': SPRITE('marshal-coen', 'front'),
  'lune': SPRITE('lune', 'front'),
  'reef': SPRITE('reef', 'front'),
  'oska-unmade': SPRITE('oska-unmade', 'front'),
  'twin-daggers-yara': SPRITE('twin-daggers-yara', 'front'),
}

interface CharacterDetailProps {
  character: Character | null
  onClose: () => void
}

const FACTION_LABELS: Record<string, string> = {
  'drifters':      'The Drifters',
  'iron-meridian': 'Iron Meridian',
  'tidecallers':   'Tidecallers',
  'wrecked':       'The Wrecked',
  'sovereign-few': 'Sovereign Few',
}

export default function CharacterDetail({ character, onClose }: CharacterDetailProps) {
  if (!character) return null

  const el = ELEMENTS[character.element]
  const templateId = (character as Character & { _templateId?: string })._templateId ?? character.id

  return (
    <Sheet open={!!character} onClose={onClose} side="bottom" title={character.name}>
      <div className="space-y-5">

        {/* Full character portrait */}
        <div className="flex justify-center">
          <div className="w-40 h-56 rounded-xl overflow-hidden bg-black/20 relative">
            <img
              src={CHAR_PORTRAITS[templateId]}
              alt={character.name}
              className="w-full h-full object-contain object-center character-float"
              style={{
                filter: `drop-shadow(0 0 16px ${el.color})`,
                imageRendering: 'crisp-edges',
              }}
              onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
            />
          </div>
        </div>

        {/* Title + faction */}
        <div>
          <p className="font-display font-bold text-sm italic mb-1" style={{ color: el.color }}>
            "{character.title}"
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`element-pill element-${character.element}`}>
              {el.emoji} {el.label}
            </span>
            <span className="text-label text-xs">
              {FACTION_LABELS[character.faction] ?? character.faction}
            </span>
            <span className="text-label text-xs capitalize">{character.class}</span>
          </div>
        </div>

        {/* Backstory */}
        <p className="text-body text-sm leading-relaxed opacity-80">
          {character.backstory}
        </p>

        <div className="divider-gold" />

        {/* Stats */}
        <div>
          <p className="text-label text-xs mb-3">Combat Stats</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Attack',  value: character.stats.attack,  color: '#FF4800' },
              { label: 'Defense', value: character.stats.defense, color: '#00F0FF' },
              { label: 'Speed',   value: character.stats.speed,   color: '#39FF14' },
              { label: 'Special', value: character.stats.special, color: el.color },
            ].map(stat => (
              <div key={stat.label} className="card-base p-3">
                <div className="flex justify-between items-center mb-1.5">
                  <p className="text-label text-xs">{stat.label}</p>
                  <p className="font-display font-black text-sm" style={{ color: stat.color }}>
                    {stat.value}
                  </p>
                </div>
                <div className="stat-bar">
                  <div
                    className="stat-bar-fill"
                    style={{
                      width: `${(stat.value / 100) * 100}%`,
                      background: `linear-gradient(90deg, ${stat.color}60, ${stat.color})`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Moves */}
        {character.moves && character.moves.length > 0 && (
          <div>
            <p className="text-label text-xs mb-3">Moves</p>
            <div className="flex flex-col gap-2">
              {character.moves.map(move => (
                <div
                  key={move.name}
                  className="card-base p-3 flex items-center justify-between"
                >
                  <div className="flex-1">
                    <p className="font-display font-bold text-sm text-ab-text">{move.name}</p>
                    {move.description && (
                      <p className="text-body text-xs opacity-70 mt-0.5">{move.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                    <span className={`element-pill element-${move.element} text-xs`}>
                      {ELEMENTS[move.element]?.emoji} {move.element}
                    </span>
                    <span
                      className="font-display font-black text-sm"
                      style={{ color: '#FF4800' }}
                    >
                      {move.power}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Companion hint */}
        {character.bondedCreatureId && (
          <div
            className="card-base p-4 border-opacity-30"
            style={{ borderColor: el.color }}
          >
            <p className="text-label text-xs mb-1">Bonded Creature</p>
            <p className="font-display font-bold text-sm" style={{ color: el.color }}>
              {character.bondedCreatureId.replace(/-/g, ' ').toUpperCase()}
            </p>
            <p className="text-body text-xs opacity-60 mt-1">
              Bond synergy active
            </p>
          </div>
        )}

        {/* Zone */}
        <div className="flex items-center gap-3 card-base p-3">
          <span className="text-xl">{el.emoji}</span>
          <div>
            <p className="text-label text-xs">Home Zone</p>
            <p className="font-display font-bold text-sm text-ab-text capitalize">
              {character.zone.replace(/-/g, ' ')}
            </p>
          </div>
        </div>

      </div>
    </Sheet>
  )
}
